/**
 * Form state for the Status Update modal (reason, comments, preferred drugs).
 * @module useStatusUpdateFormState
 */

import { useMemo, useState } from "react";
import {
  PreferredDrugFormState,
  PreferredJcodeEntry,
} from "../statusUpdateModal.types";
import { buildPreferredDrugOptions } from "../utils/buildPreferredDrugOptions";
import { createEmptyPreferredJcodeEntry } from "../utils/createEmptyPreferredJcodeEntry";

/**
 * Owns Status Update modal form state and synchronizes preferred J-code rows
 * with the tried/failed MultiSelect selection.
 *
 * Exposes `preferredDrugForm` with `preferredJcodeEntries`,
 * `preferredDrugValues`, `stepTherapyCount`, and `source` for staging;
 * camelCase form fields are mapped to snake_case `additional_info` on save.
 * Also exposes `isAllRequiredFieldsHaveValue` for Save enablement when
 * preferred-drug input is required.
 *
 * Empty preferred J-code rows are created via
 * {@link createEmptyPreferredJcodeEntry} (kept outside the input component
 * module to avoid circular imports).
 *
 * @return Form state values, setters, preferred-drug snapshot, handlers, and
 *   required-field completeness flag
 */
export const useStatusUpdateFormState = () => {
  const [reason, setReason] = useState<string | null>(null);
  const [textareaContent, setTextareaContent] = useState<string>("");
  const [preferredJcodeEntries, setPreferredJcodeEntries] = useState<
    PreferredJcodeEntry[]
  >([createEmptyPreferredJcodeEntry()]);
  const [preferredDrugValues, setPreferredDrugValues] = useState<string[]>([]);
  const [stepTherapyCount, setStepTherapyCount] = useState<string>("");
  const [source, setSource] = useState<string>("");

  const preferredDrugForm = useMemo<PreferredDrugFormState>(
    () => ({
      preferredJcodeEntries,
      preferredDrugValues,
      stepTherapyCount,
      source,
    }),
    [preferredJcodeEntries, preferredDrugValues, stepTherapyCount, source],
  );

  /**
   * Whether every currently required preferred-drug field has a value.
   *
   * Required for now: `preferredJcodeEntries` (at least one row with a
   * non-empty J-code and drug name), `stepTherapyCount`, and `source`.
   *
   * @return `true` when all required fields are filled
   */
  const isAllRequiredFieldsHaveValue = useMemo(() => {
    const hasPreferredJcodeEntry = preferredJcodeEntries.some(
      (entry) =>
        entry.jCode.trim().length > 0 && entry.drugName.trim().length > 0,
    );
    return (
      hasPreferredJcodeEntry &&
      stepTherapyCount.trim().length > 0 &&
      source.trim().length > 0
    );
  }, [preferredJcodeEntries, stepTherapyCount, source]);

  /**
   * Updates preferred J-code rows and drops MultiSelect values whose J-codes
   * are no longer present in the derived options.
   *
   * @input entries - Next preferred J-code rows
   * @return void
   */
  const handlePreferredJcodeEntriesChange = (
    entries: PreferredJcodeEntry[],
  ) => {
    setPreferredJcodeEntries(entries);
    const validJCodes = new Set(
      buildPreferredDrugOptions(entries).map((option) => option.value),
    );
    setPreferredDrugValues((prev) =>
      prev.filter((value) => validJCodes.has(value)),
    );
  };

  return {
    reason,
    setReason,
    textareaContent,
    setTextareaContent,
    preferredDrugForm,
    setPreferredDrugValues,
    setStepTherapyCount,
    setSource,
    handlePreferredJcodeEntriesChange,
    isAllRequiredFieldsHaveValue,
  };
};
